/**
 * ActivityLocationMap — interactive Google Maps JS API widget.
 * Same engine as DonateUsedItemPage. Fully draggable, pinch-zoom, pan controls.
 * Resolves location via: lat/lng → map_link URL parse → backend short-URL resolver.
 */
import { useEffect, useRef, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BLURU_BOUNDS = {
  north: 13.2, south: 12.7, east: 77.85, west: 77.35,
};

function loadMapsScript(apiKey, onReady) {
  if (window.google?.maps?.Map) { onReady(); return; }
  const existing = document.getElementById('gmap-script');
  if (existing) {
    if (window.google?.maps?.Map) { onReady(); return; }
    existing.addEventListener('load', () => onReady());
    return;
  }
  const s = document.createElement('script');
  s.id    = 'gmap-script';
  s.src   = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&v=weekly`;
  s.async = true;
  s.onload = () => onReady();
  document.head.appendChild(s);
}

function extractCoordsFromLink(url) {
  if (!url) return null;
  // /@lat,lng
  const atMatch = url.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
  if (atMatch) return { lat: parseFloat(atMatch[1]), lng: parseFloat(atMatch[2]) };
  // ?q=lat,lng
  const qMatch = url.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
  if (qMatch) return { lat: parseFloat(qMatch[1]), lng: parseFloat(qMatch[2]) };
  // !3dlat!4dlng
  const embedMatch = url.match(/!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/);
  if (embedMatch) return { lat: parseFloat(embedMatch[1]), lng: parseFloat(embedMatch[2]) };
  return null;
}

function isShortUrl(url) {
  return url && (url.includes('goo.gl') || url.includes('maps.app.goo.gl'));
}

const ActivityLocationMap = ({ latitude, longitude, mapLink, address, className = '', style = {} }) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [ready, setReady] = useState(!!window.google?.maps?.Map);
  const [resolvedCoords, setResolvedCoords] = useState(null);
  const [resolving, setResolving] = useState(false);

  // Immediate coords from props or URL parse
  const directCoords =
    (latitude && longitude)
      ? { lat: parseFloat(latitude), lng: parseFloat(longitude) }
      : extractCoordsFromLink(mapLink);

  // Resolve short URL via backend if needed
  useEffect(() => {
    if (directCoords || !isShortUrl(mapLink) || resolvedCoords || resolving) return;
    setResolving(true);
    fetch(`${API_URL}/api/activities/resolve-map-link?url=${encodeURIComponent(mapLink)}`)
      .then(r => r.json())
      .then(d => {
        if (d.lat && d.lng) setResolvedCoords({ lat: d.lat, lng: d.lng });
      })
      .catch(() => {})
      .finally(() => setResolving(false));
  }, [mapLink]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!apiKey) return;
    loadMapsScript(apiKey, () => setReady(true));
  }, [apiKey]);

  useEffect(() => {
    if (!ready || !mapRef.current || mapInstanceRef.current) return;
    const coords = directCoords || resolvedCoords;
    if (!coords) return;

    try {
      const map = new window.google.maps.Map(mapRef.current, {
        center: coords,
        zoom: 15,
        restriction: { latLngBounds: BLURU_BOUNDS, strictBounds: false },
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });
      mapInstanceRef.current = map;

      new window.google.maps.Marker({
        position: coords,
        map,
        title: address || '',
      });
    } catch (err) {
      console.error('Map init error', err);
    }
  }, [ready, resolvedCoords]); // eslint-disable-line react-hooks/exhaustive-deps

  const coords = directCoords || resolvedCoords;
  if (!coords && !resolving) return null;
  if (!apiKey) return null;

  return (
    <div
      ref={mapRef}
      className={className}
      style={{ width: '100%', height: 160, ...style }}
      data-testid="activity-location-map"
    />
  );
};

export default ActivityLocationMap;
